<template>
  <div>
    <component 
      :is="currentView" 
      :loading="loading" 
      :events="events" 
      @refetch-events-for-watchlist="refetchEventsForWatchlist"
    />
  </div>
</template>

<script>
import { markRaw } from 'vue';
import { breakpointsTailwind, useBreakpoints } from '@vueuse/core';
import EventTable from './EventTable.vue';
import EventTiles from './EventTiles.vue';
import {
  store,
  setTableState,
  resetTableStateEvents,
  getEventsMap,
  fetchSubscriptionsToGlobalState,
  updatePrices
} from '../store';
import apiClient from '../utils/axios';
import ms from 'ms';
import MobileEventTable from './MobileEventTable.vue';
export default {
  name: 'EventsContainer',

  components: {
    EventTable,
    EventTiles,
    MobileEventTable
  },

  data() {
    const breakpoints = useBreakpoints(breakpointsTailwind);

    return {
      loading: false,
      priceUpdateInterval: null,
      events: [],
      abortController: null,
      isMobile: breakpoints.smallerOrEqual('sm'),
      screenWidth: window.innerWidth,
    }
  },

  computed: {
    currentView() {
      return markRaw(this.isMobile ? MobileEventTable : EventTable);
      //return markRaw(EventTable);
    }
  },

  watch: {
    '$route.params': {
      async handler(newParams, oldParams) {
        if (this.abortController) {
          console.log('aborting load');
          this.abortController.abort();
          this.abortController = null;
        }

        if (newParams.marketName && newParams.marketSlug &&
          (newParams.marketName !== oldParams?.marketName ||
            newParams.marketSlug !== oldParams?.marketSlug)) {

          resetTableStateEvents();
          await this.fetchAllEvents(newParams.marketName, newParams.marketSlug);
        }

        if (newParams.listId && newParams.listId !== oldParams?.listId) {
          await this.refetchEventsForWatchlist(newParams.listId);
        }

      },
      immediate: true
    },
  },

  methods: {
    async refetchEventsForWatchlist(listId) {
      // First, check if the watchlist exists
      const watchList = store.watchLists.find(item => item.id === listId);
      if (watchList) {
        store.currentWatchList = watchList;
        store.currentWatchListId = listId;
      } else {
        this.$router.push(`/`);
      }

      resetTableStateEvents();
      await this.fetchDashboardData(listId);

      if (this.priceUpdateInterval) {
        clearInterval(this.priceUpdateInterval);
        this.priceUpdateInterval = null;
      }

      setTimeout(async () => {
        await updatePrices();
        this.priceUpdateInterval = setInterval(async () => {
          await updatePrices();
        }, ms('5m'));
      }, ms('5s'));
    },

    async fetchAllEvents(marketName, marketSlug) {
      this.loading = true;
      this.abortController = new AbortController();

      const label = marketSlug.replace(/-/g, " ");
      const title = `${this.capitalizeEachWord(marketName)} :: ${this.capitalizeEachWord(label)}`;
      await setTableState([], title);

      try {
        // add header X-IMG-CACHE: true
        const response = await fetch(
          `https://api.nevua.markets/markets/token-prices-history/${marketName}/${marketSlug}`,
          { signal: this.abortController.signal, headers: { 'X-IMG-CACHE': 'true' } }
        );

        if (this.abortController.signal.aborted) {
          console.log('Request was aborted, skipping data processing');
          return;
        }

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const data = await response.json();
        setTableState(data, title);
      } catch (error) {
        if (error.name === 'AbortError') {
          console.log('Fetch aborted');
          return;
        }
        Sentry.setExtra('method', 'fetchAllEvents');
        Sentry.captureException(error);
      } finally {
        this.loading = false;
      }
    },

    async fetchDashboardData(listId) {
      this.loading = true;
      this.abortController = new AbortController();

      const watchList = store.watchLists.find(item => item.id === listId);

      try {
        const eventsMap = getEventsMap(watchList, []);
        const response = await apiClient.post('https://api.nevua.markets/markets/token-prices-history', eventsMap, {
          signal: this.abortController.signal,
          headers: { 'X-IMG-CACHE': 'true' }
        });

        if (this.abortController.signal.aborted) {
          console.log('Request was aborted, skipping data processing');
          return;
        }

        if (store.nevuaSubscriptions.length === 0) {
          // Lazy load subscriptions to global state
          await fetchSubscriptionsToGlobalState();
        }

        const data = response.data;
        setTableState(data, watchList?.name, listId);

      } catch (error) {
        if (error.name === 'CanceledError') {
          console.log('Fetch aborted');
          return;
        }
        console.error('Error fetching dashboard data:', error);
      } finally {
        this.loading = false;
        store.loadingWatchlistId = null;
      }
    },

    capitalizeEachWord(sentence) {
      return sentence.replace(/\b\w/g, char => char.toUpperCase());
    },

    updateScreenWidth() {
      this.screenWidth = window.innerWidth;
    }
  },

  mounted() {
    window.addEventListener('resize', this.updateScreenWidth);
  },

  beforeUnmount() {
    window.removeEventListener('resize', this.updateScreenWidth);
    if (this.abortController) {
      this.abortController.abort();
      this.abortController = null;
    }

    if (this.priceUpdateInterval) {
      clearInterval(this.priceUpdateInterval);
      this.priceUpdateInterval = null;
    }
  }
}
</script>